import { createSlice, isAnyOf } from '@reduxjs/toolkit';

import { ASSISTANTS_CATEGORY, GENIE_EVALUATE_TYPE } from '../../constants';
import {
  getGenieGoData,
  resetSession,
  predictGenieGoResponse,
  generateGenieGoMessages,
  replyGenieGo,
  evaluateGeneratedMessages,
} from './genieGoActions';
import {
  evaluateGeneratedMessagesConsumer,
  generateConsumerGenieGoMessages,
  getConsumerGenieGoData,
  predictConsumerGenieGoResponse,
  replyConsumerGenieGo,
  resetGenieGoSession,
} from '../consumer/genie-go/consumerGenieGoActions';

const genieGoInitialState = {
  brand: '',
  projectName: '',
  projectId: '',
  threadId: '',

  isPredicting: false,
  isGeneratingAlternate: false,
  predictedInitMessage: null,
  predictedMessages: [],

  channels: {
    [ASSISTANTS_CATEGORY.GENERATE_ALL]: {
      hint: '',
    },
  },

  assistants: {
    [ASSISTANTS_CATEGORY.GENERATE_ALL]: [],
  },

  isFormLoading: false,
  isGenerating: false,
  isResetting: false,
  messages: [],

  evaluateActions: {
    [GENIE_EVALUATE_TYPE.GENERATED]: {
      pending: false,
      abortController: null,
    },
  },
};

const genieGoSlice = createSlice({
  name: 'genieGo',
  initialState: genieGoInitialState,
  reducers: {
    updateEvaluatedGeneratedMessages: (state, action) => {
      const newMessages = action.payload;
      const storedMessages = state.messages;

      state.messages = storedMessages.map((storedMessage) => {
        const updatedMessage = newMessages.find(({ message }) => message === storedMessage.messageText);

        return updatedMessage ? { ...storedMessage, ...updatedMessage } : storedMessage;
      });
    },

    setEvalAbortController: (state, action) => {
      state.evaluateActions[action.payload.type].abortController = action.payload.controller;
    },
    cancelEvaluationByType: (state, action) => {
      const evaluationType = action.payload;

      if (state.evaluateActions[evaluationType].abortController) {
        state.evaluateActions[evaluationType].abortController.abort();
        state.evaluateActions[evaluationType].abortController = null;
      }

      state.evaluateActions[evaluationType].pending = false;
    },

    resetGenieGoParams: () => genieGoInitialState,
  },
  extraReducers: (builder) => {
    builder

      .addCase(getGenieGoData.fulfilled, (state, action) => {
        const { assistants, brand, projectName, projectId } = action.payload;
        state.assistants = assistants;
        state.brand = brand;
        state.projectName = projectName;
        state.projectId = projectId;
      })

      .addCase(getConsumerGenieGoData.fulfilled, (state, action) => {
        const { assistants, brand, projectName } = action.payload;
        state.assistants = assistants;
        state.brand = brand;
        state.projectName = projectName;
      })

      .addMatcher(isAnyOf(resetSession.fulfilled, resetGenieGoSession.fulfilled), (state) => {
        state.isResetting = false;
        state.messages = [];
        state.channels = genieGoInitialState.channels;
      })
      .addMatcher(isAnyOf(resetSession.pending, resetGenieGoSession.pending), (state) => {
        state.isResetting = true;
      })
      .addMatcher(isAnyOf(resetSession.rejected, resetGenieGoSession.rejected), (state) => {
        state.isResetting = false;
      })

      .addMatcher(isAnyOf(replyGenieGo.fulfilled, replyConsumerGenieGo.fulfilled), (state, action) => {
        const { messages, threadId, hint } = action.payload;

        const formattedNewMessages = messages.map((message, index) => ({
          messageText: message,
          count: state.messages.length + messages.length - index,
          isNew: true,
        }));

        const formattedOldMessages = state.messages.map(({ messageText, count, interest, action, uncertainty }) => ({
          messageText,
          count,
          isNew: false,
          interest,
          action,
          uncertainty,
        }));

        state.messages = [...formattedNewMessages, ...formattedOldMessages];
        state.threadId = threadId;
        state.channels[ASSISTANTS_CATEGORY.GENERATE_ALL].hint = hint;
      })

      .addMatcher(
        isAnyOf(generateGenieGoMessages.fulfilled, generateConsumerGenieGoMessages.fulfilled),
        (state, action) => {
          const { messages, threadId, hint } = action.payload;

          const formattedNewMessages = messages.map((message, index) => ({
            messageText: message,
            count: messages.length - index,
            isNew: true,
          }));

          state.messages = [...formattedNewMessages];
          state.threadId = threadId;
          state.channels[ASSISTANTS_CATEGORY.GENERATE_ALL].hint = hint;

          state.isGenerating = false;
        },
      )
      .addMatcher(isAnyOf(generateGenieGoMessages.pending, generateConsumerGenieGoMessages.pending), (state) => {
        state.isGenerating = true;
      })
      .addMatcher(isAnyOf(generateGenieGoMessages.rejected, generateConsumerGenieGoMessages.rejected), (state) => {
        state.isGenerating = false;
      })

      .addMatcher(
        isAnyOf(predictGenieGoResponse.fulfilled, predictConsumerGenieGoResponse.fulfilled),
        (state, action) => {
          state.isPredicting = false;
          state.isGeneratingAlternate = false;

          state.predictedInitMessage = action.payload.predictedInitMessage;
          const newPredictedMessages = action.payload.predictedMessages ?? [];

          state.predictedMessages = newPredictedMessages.map((message, index) => ({
            ...message,
            count: newPredictedMessages.length - index,
          }));
        },
      )
      .addMatcher(isAnyOf(predictGenieGoResponse.pending, predictConsumerGenieGoResponse.pending), (state) => {
        state.isPredicting = true;
        state.isGeneratingAlternate = true;
      })
      .addMatcher(isAnyOf(predictGenieGoResponse.rejected, predictConsumerGenieGoResponse.rejected), (state) => {
        state.isPredicting = false;
        state.isGeneratingAlternate = false;
      })

      .addMatcher(isAnyOf(evaluateGeneratedMessages.pending, evaluateGeneratedMessagesConsumer.pending), (state) => {
        state.evaluateActions[GENIE_EVALUATE_TYPE.GENERATED].pending = true;
      })
      .addMatcher(
        isAnyOf(evaluateGeneratedMessages.fulfilled, evaluateGeneratedMessagesConsumer.fulfilled),
        (state) => {
          state.evaluateActions[GENIE_EVALUATE_TYPE.GENERATED].pending = false;
          state.evaluateActions[GENIE_EVALUATE_TYPE.GENERATED].abortController = null;
        },
      )
      .addMatcher(isAnyOf(evaluateGeneratedMessages.rejected, evaluateGeneratedMessagesConsumer.rejected), (state) => {
        state.evaluateActions[GENIE_EVALUATE_TYPE.GENERATED].pending = false;
        state.evaluateActions[GENIE_EVALUATE_TYPE.GENERATED].abortController = null;
      })

      .addMatcher(
        isAnyOf(
          getGenieGoData.fulfilled,
          resetSession.fulfilled,

          generateGenieGoMessages.fulfilled,
          replyGenieGo.fulfilled,
          getConsumerGenieGoData.fulfilled,
          generateConsumerGenieGoMessages.fulfilled,
          replyConsumerGenieGo.fulfilled,
        ),
        (state) => {
          state.isFormLoading = false;
        },
      )
      .addMatcher(
        isAnyOf(
          getGenieGoData.pending,
          resetSession.pending,

          generateGenieGoMessages.pending,
          replyGenieGo.pending,
          getConsumerGenieGoData.pending,
          generateConsumerGenieGoMessages.pending,
          replyConsumerGenieGo.pending,
        ),
        (state) => {
          state.isFormLoading = true;
        },
      )
      .addMatcher(
        isAnyOf(
          getGenieGoData.rejected,
          resetSession.rejected,

          generateGenieGoMessages.rejected,
          replyGenieGo.rejected,
          getConsumerGenieGoData.rejected,
          generateConsumerGenieGoMessages.rejected,
          replyConsumerGenieGo.rejected,
        ),
        (state) => {
          state.isFormLoading = false;
        },
      );
  },
});

export const { resetGenieGoParams, setEvalAbortController, cancelEvaluationByType, updateEvaluatedGeneratedMessages } =
  genieGoSlice.actions;

export default genieGoSlice.reducer;
